<template>
  <div class="selectUnit">
    <div class="selectUnit__header">
      <div class="header__back" v-show="isCollaboratorFlow" @click="backAction">
        <ArrowRightSvg />
      </div>
      <div class="header__icon">
        <PinSvg />
      </div>
      <h1>Escolha uma unidade</h1>
    </div>

    <div class="selectUnit__list" v-if="unitsList && unitsList.length && !listError">
      <ul class="list__card">
        <li
          :class="['card__item', { 'item--selected': unit.idUnidade === unitSelected.idUnidade }]"
          v-for="(unit, idx) in unitsList"
          :key="unit.idUnidade"
          @click="unitSelection(unit)"
        >
          <div v-if="isGeolocalActive && unit.distancia" class="item__local">
            <div class="local__distance">
              {{
                `${unit.distancia === 150 ? '>' : ''}
                            ${unit.distancia} KM`
              }}
            </div>
            <p class="local__closest" v-if="idx === 0">
              <PinSvg />
              Esta é a unidade mais próxima a você
            </p>
          </div>
          <h2 class="item__title">{{ unit.nome | capitalizeText }}</h2>
          <div v-if="unit.endereco && unit.endereco.cidade">
            <p class="item__address" v-html="unitFullAddress(unit.endereco).fullAddress" />
            <a
              class="item__link"
              :href="unitFullAddress(unit.endereco).googleLinkAddress"
              target="_blank"
            >
              Ver endereço
            </a>
          </div>
          <div v-else>
            <p class="item__address">Unidade com endereço indisponível</p>
          </div>
        </li>
      </ul>
    </div>

    <div v-else-if="listError" class="selectUnit__error">
      <p>
        Não foi possível carregar a lista de unidades disponíveis.
        <br />
        <span> Recarregue e tente novamente! </span>
      </p>
    </div>

    <div v-else>
      <p>Nenhuma unidade disponível</p>
    </div>

    <div class="selectUnit__button">
      <VButton
        text="Continuar"
        :disabled="continueButtonAction"
        @click.native="continueToCheckIn()"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex';
import capitalizeText from '@/utils/helpers/filters/capitalizeText';
import unitDataGeolocation from './helpers/unitDataGeolocation';
import { Units } from '@/utils/enums/validUnitsEnum/unitEnum';
import { LOCAL_STORAGE_UNIT_ID } from '@/utils/constants/checkin.constants';
import { IS_COLLABORATOR_FLOW } from '@/utils/constants/datalayerMessage.constants';
import { dataLayersMixin } from '@/utils/mixins';
import datalayerErrorMessage from '@/utils/helpers/message/datalayerErrorMessage.js';

export default {
  name: 'SelectUnit',
  mixins: [dataLayersMixin],
  data: () => ({
    continueButtonAction: true,
    unitsList: null,
    unitSelected: {},
    isGeolocalActive: false,
    listError: false,
    cpf: '',
  }),
  async mounted() {
    await this.unitsListGetter();
    this.mostCommonPagesAnalytics({
      entireObject: {
        event: 'view',
        context: {
          path: this.$route.path,
          step: 'escolha_de_unidade',
          screen: 'escolha_de_unidade',
        },
        admission: {
          type: 'nao-agendado',
          brand_id: this.brandId,
          brand_name: this.brandName,
        },
      },
    });
  },
  computed: {
    ...mapState({
      brandId: ({ brandUnit }) => brandUnit.data.currentBrand.brandId,
      brandName: ({ brandUnit }) => brandUnit.data.currentBrand.brandName,
      unitsListState: ({ brandUnit }) => brandUnit.data.unitsList,
      admission: ({ currentAdmission }) => currentAdmission.data,
      mdmKeycloak: ({ mdm }) => mdm.keycloak,
    }),

    ...mapGetters({
      isT2FlowAllowed: 'global/isT2FlowAllowed',
      isCollaboratorFlow: 'global/isCollaboratorFlow',
      getBrandUnitIdVariables: 'getBrandUnitIdVariables',
      selectedBrand: 'selectedBrand',
    }),

    unitWithCollaboratorsCampaign() {
      return this.unitsListState;
    },

    validUnitsFilter() {
      let units = [];

      const validUnits = Object.values(Units).map((unit) => parseInt(unit.unitId));
      units = this.unitsListState.filter((unit) => validUnits.includes(unit.idUnidade));

      return units;
    },
  },
  methods: {
    ...mapActions(['dataProviderGetUnits', 'unitConfigGetUnits']),
    ...mapMutations({
      // TO-DO: Check Merge refering to SHAZ-850
      setUnit: 'SET_UNIT',
      setAdmission: 'SET_ADMISSION_DATA',
      setCurrentUnitVariables: 'SET_CURRENT_UNIT_NAME_ID',
      setClearCurrentBrand: 'SET_CLEAR_CURRENT_BRAND',
      setClearCurrentUnit: 'SET_CLEAR_CURRENT_UNIT',
    }),
    /**
     * @method unitsListGetter
     * @description
     * Sets units list (Data Provider API)
     */
    async unitsListGetter() {
      try {
        if (this.isCollaboratorFlow) {
          const params = {
            brandId: this.brandId,
            collaboratorCampaign: this.isCollaboratorFlow,
          };
          await this.unitConfigGetUnits(params);
        } else {
          await this.dataProviderGetUnits({ idMarca: parseInt(this.brandId) });
        }

        if (!this.isCollaboratorFlow) {
          this.unitsList = this.validUnitsFilter;
        } else {
          this.unitsList = this.unitWithCollaboratorsCampaign;
        }

        const unitsWithDistance = await unitDataGeolocation.getUnitsListWithDistanceProperty(
          this.unitsList,
        );
        const isGeolocalActive = Array.isArray(unitsWithDistance);

        if (isGeolocalActive) {
          this.isGeolocalActive = true;
          this.unitsList = await this.geoLocalSorter(unitsWithDistance);
        }
      } catch (error) {
        this.listError = true;
        console.log(error);
      }
    },

    /**
     * @method geoLocalSorter
     * @description
     * Sorts list of units with distance/location property
     */
    geoLocalSorter(unitsWithDistance) {
      return unitsWithDistance.sort((firstItem, secondItem) =>
        !secondItem.distancia
          ? -1
          : parseFloat(firstItem.distancia) - parseFloat(secondItem.distancia),
      );
    },

    /**
     * @method unitFullAddress
     * @description
     * Formats an unit's address to be displayed in full format
     */
    unitFullAddress(unitAddressData) {
      const {
        logradouro: unitAddress,
        numero: unitNumber,
        bairro: unitNeighborhood,
        cidade: unitCity,
        uf: unitState,
        cep: unitPostalCode,
      } = unitAddressData;

      const searchAddress = `${unitAddress}, ${unitNumber}, ${unitPostalCode}`;
      const googleLinkAddress = `https://www.google.com/maps/search/${searchAddress}`;

      const fullAddress = `
        ${capitalizeText(unitAddress)},
        ${unitNumber} -
        ${capitalizeText(unitNeighborhood)},
        ${capitalizeText(unitCity)} -
        ${unitState} -
        ${unitPostalCode}
      `;

      return {
        fullAddress,
        googleLinkAddress,
      };
    },

    /**
     * @method unitSelection
     * @description
     * Activates button and adds class to current selected item
     */
    unitSelection(unit) {
      this.unitSelected = unit;
      this.continueButtonAction = false;
    },

    /**
     * @method continueToCheckIn
     * @description
     * Triggers route push and sets unitId in store
     */
    continueToCheckIn() {
      const brand = this.brandId;
      const unit = this.unitSelected.idUnidade.toString();
      this.setUnit(this.unitSelected);

      if (this.isCollaboratorFlow) {
        this.setCurrentUnitVariables({ unitId: this.unitSelected.idUnidade });
        localStorage.setItem(LOCAL_STORAGE_UNIT_ID, this.unitSelected.idUnidade);

        this.goNextStep(
          'check_service_desk',
          datalayerErrorMessage('continueToCheckIn', IS_COLLABORATOR_FLOW),
        );
      } else {
        this.$router.push({ name: 'checkIn', params: { brandId: brand, unitId: unit } });
      }
    },

    backAction() {
      this.setClearCurrentBrand();
      this.setClearCurrentUnit();
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss">
.selectUnit {
  max-width: 420px;
  height: 100%;
  margin: 0 auto;
  overflow: hidden;

  svg {
    * {
      fill: var(--color-primary);
    }
  }

  &__header {
    align-items: center;
    display: flex;
    flex-direction: row;
    margin-top: 8px;
    padding-bottom: 12px;

    h1 {
      padding-left: 18px;
      font-size: var(--font-size-4);
      font-weight: bold;
    }

    .header {
      &__back {
        margin-right: 18px;

        svg {
          width: 14px;
          transform: rotate(180deg);

          path {
            fill: #1616163d;
          }
        }
      }

      &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        width: 40px;
        border-radius: 8px;
        background-color: var(--color-primary-transparency);

        svg {
          width: 15px;
          height: 20px;
        }
      }
    }
  }

  .list__card {
    max-height: 82vh;
    padding-bottom: 88px;
    overflow-y: auto;

    .card__item {
      display: flex;
      flex-direction: column;
      position: relative;
      width: 100%;
      min-height: 96px;
      max-height: 156px;
      padding: 16px;
      margin-top: 16px;
      border: 1px solid #1616161f;
      border-radius: 4px;
      text-align: left;
      cursor: pointer;

      .item {
        &__local {
          .local__distance {
            position: absolute;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 56px;
            max-width: 62px;
            height: 24px;
            right: 16px;
            border-radius: 24px;
            background-color: var(--color-grey-3);
            font-size: var(--font-size-1);
          }

          .local__closest {
            padding-bottom: 8px;
            font-size: var(--font-size-1);
            color: var(--color-primary);

            svg {
              height: 10px;
              padding-right: 2px;
            }
          }
        }

        &__title {
          font-size: var(--font-size-3);
          font-weight: bold;
          margin-bottom: 14px;
          max-width: 78%;
        }

        &__address {
          font-size: var(--font-size-1);
          color: var(--color-grey-7);
        }

        &__link {
          align-self: flex-end;
          color: var(--color-grey-7);
          padding: 6px 4px 0 0;
          font-size: var(--font-size-1);
          text-decoration: underline;
        }
      }

      @media (max-width: 320px) {
        padding: 10px;

        .item {
          &__local {
            .local__distance {
              min-width: 44px;
              max-width: 46px;
              height: 20px;
              right: 10px;
              font-size: 10px;
            }
          }

          &__link {
            padding: 8px 0 0;
          }

          &__title {
            font-size: var(--font-size-2);
            width: 76%;
          }
        }
      }
    }

    .item--selected {
      background-color: var(--color-primary-transparency);
      border: 1px solid var(--color-primary);
      transition: 0.5s ease-in-out;
    }
  }

  .selectUnit__button {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 24px 16px;
    max-width: inherit;
    margin: 0 auto;
  }

  &__error {
    padding-top: 30vh;

    p {
      width: 90%;
      margin: 0 auto;
    }

    span {
      background-color: var(--color-primary-transparency);
      padding: 0 16px;
    }
  }
}
</style>
